import { z } from "zod";
import { assertThat } from "./assert-that.ts";
import { Temporal } from "./temporal.ts";

export type PlainDate = Temporal.PlainDate;

export const isPlainDate = (value: unknown): value is PlainDate => value instanceof Temporal.PlainDate;

export function plainDateFromISO(value: string): PlainDate {
  return Temporal.PlainDate.from(value);
}

export function plainDateToISO(date: PlainDate): string {
  return date.toJSON();
}

export function plainDateFromUtcDate(date: Date): PlainDate {
  return plainDateFromISO(date.toISOString().slice(0, 10));
}

export function plainDateToUtcDate(date: PlainDate): Date {
  return new Date(Date.UTC(date.year, date.month - 1, date.day, 12, 0, 0));
}

export function plainDateTodayUtc(now = new Date()): PlainDate {
  return plainDateFromUtcDate(now);
}

export function comparePlainDate(left: PlainDate, right: PlainDate): number {
  return Temporal.PlainDate.compare(left, right);
}

const TEMPORAL_WEEKEND_DAYS = new Set([
  6, // Saturday
  7, // Sunday
]);

const isWeekDay = (day: number): boolean => !TEMPORAL_WEEKEND_DAYS.has(day);

export function addBusinessDaysToPlainDate(date: PlainDate, amount: number): PlainDate {
  assertThat(amount >= 0, "Amount of days added must be positive or null", { amount });

  let result = date;
  let remaining = amount;

  while (remaining > 0) {
    result = result.add({ days: 1 });
    if (isWeekDay(result.dayOfWeek)) {
      remaining -= 1;
    }
  }

  return result;
}

export const plainDateSchema = z.custom<PlainDate>((value) => isPlainDate(value), {
  error: "Expected a Temporal.PlainDate",
});

export const plainDateFromStringSchema = z.string().transform((value, ctx) => {
  const trimmed = value.trim();

  try {
    return plainDateFromISO(trimmed);
  } catch {
    ctx.addIssue({ code: "custom", message: `Expected a valid PlainDate, got "${value}"` });
    return z.NEVER;
  }
});

export function tryParsePlainDate(value: string | null | undefined): PlainDate | null {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return null;
  }

  try {
    return plainDateFromISO(trimmed);
  } catch {
    return null;
  }
}
